import React from 'react'
import {Trans} from 'react-i18next'

import {Grid, Typography} from '@mui/material'
import {makeStyles} from 'tss-react/mui'

import {ReactComponent as FileIcon} from '@progyny/assets/icons/file.svg'
import {ReactComponent as HeartMessageIcon} from '@progyny/assets/icons/heart-message.svg'
import {ReactComponent as MidwifeIcon} from '@progyny/assets/icons/midwife.svg'
import {useConsultationsProgramDetailQuery} from '@progyny/generated/graphql'
import PregnancyAssessment from '@progyny/pages/Consultations/Pregnancy/PregnancyAssessment'
import ConsultationPageLayout from '@progyny/pages/Consultations/components/ConsultationPageLayout'
import ConsultationRoutes from '@progyny/pages/Consultations/components/ConsultationRoutes'
import P3ConsultationViewContent, {
  type P3ActiveTab,
} from '@progyny/pages/Consultations/components/P3ConsultationViewContent'
import ProgramCard from '@progyny/pages/Consultations/components/ProgramCard'
import {PREGNANCY_HUB_PROGRAM_SLUGS} from '@progyny/pages/Consultations/constants'

const PREGNANCY_BASE_PATH = '/consultations/pregnancy'

const useStyles = makeStyles()((theme) => ({
  cardsHeadline: {
    width: '100%',
    maxWidth: '1256px',
    height: '24px',
    fontFamily: 'Poppins',
    fontStyle: 'normal',
    fontWeight: 600,
    fontSize: '18px',
    lineHeight: '24px',
    color: '#251F1F',
    marginBottom: theme.spacing(1),
  },
}))

const PregnancyProgramsOverview: React.FC<{
  colors: any
}> = ({colors}) => {
  const {classes} = useStyles()

  const {data: pregnancyProgramData} = useConsultationsProgramDetailQuery({
    variables: {slug: 'pregnancy-support'},
  })

  const pregnancyProgram = pregnancyProgramData?.consultations?.program
  const pregnancyEnrollment =
    pregnancyProgramData?.consultations?.myProgramEnrollments?.find(
      (e) => e?.program?.id === pregnancyProgram?.id
    )
  const session8Status = pregnancyEnrollment?.sessionStatuses?.find(
    (ss) => ss?.session?.sessionNumber === 8
  )
  const isPostpartumLocked = !(
    session8Status?.status === 'BOOKED' ||
    session8Status?.status === 'COMPLETED'
  )

  return (
    <Grid container rowGap={2}>
      <Typography className={classes.cardsHeadline} component='h4'>
        <Trans i18nKey='consultations_postpartum_postpartum_consultation_page_your_program' />
      </Typography>
      <Grid container spacing={3}>
        <ProgramCard
          slug='pregnancy-support'
          icon={FileIcon}
          color='dark'
          colors={colors}
          fullWidth
          isFullRow
        />
      </Grid>

      <Typography className={classes.cardsHeadline} component='h4' mt={4}>
        <Trans i18nKey='consultations_postpartum_postpartum_consultation_page_additional_suppo' />
      </Typography>
      <Grid container spacing={3}>
        <ProgramCard
          slug='pregnancy-emotional-support-program'
          icon={HeartMessageIcon}
          color='bright'
          colors={colors}
        />
        <ProgramCard
          slug='pregnancy-ask-a-midwife'
          isAskAMidwife
          icon={MidwifeIcon}
          color='bright'
          colors={colors}
        />
      </Grid>

      <Typography className={classes.cardsHeadline} component='h4' mt={4}>
        <Trans i18nKey='consultations_pregnancy_pregnancy_consultation_page_postpartum_care_jo' />
      </Typography>
      <Grid container spacing={3}>
        <ProgramCard
          slug='postpartum-support'
          icon={FileIcon}
          color='dark'
          colors={colors}
          fullWidth
          locked={isPostpartumLocked}
          isFullRow
        />
      </Grid>
    </Grid>
  )
}

const PregnancyViewWrapper: React.FC<{activeTab: P3ActiveTab}> = ({
  activeTab,
}) => (
  <ConsultationPageLayout consultationSlug='pregnancy'>
    {(consultation, colors, theme, refetchConsultationDetail) => (
      <P3ConsultationViewContent
        basePath={PREGNANCY_BASE_PATH}
        consultation={consultation}
        colors={colors}
        theme={theme}
        activeTab={activeTab}
        hub={{
          onboardingConsultationSlug: 'pregnancy',
          programSlugs: PREGNANCY_HUB_PROGRAM_SLUGS,
        }}
        overview={<PregnancyProgramsOverview colors={colors} />}
        onRefetchConsultationDetail={refetchConsultationDetail}
      />
    )}
  </ConsultationPageLayout>
)

const PregnancyOverview: React.FC = () => (
  <PregnancyViewWrapper activeTab='overview' />
)

const PregnancyConsultationPage: React.FC = () => {
  return (
    <ConsultationRoutes
      consultationSlug='pregnancy'
      assessmentComponent={PregnancyAssessment}
      overviewComponent={PregnancyOverview}
      onboardingSlugOverride='pregnancy-support'
      upcomingPastView={({activeTab}) => (
        <PregnancyViewWrapper activeTab={activeTab} />
      )}
    />
  )
}

export default PregnancyConsultationPage
